import React, { useCallback, useEffect, useState } from "react";
import { ButtonOutline, Heading } from "../../../components/styledConstants";
import { Box, CircularProgress } from "@mui/material";
import AddOrgCardModal from "./Components/AddOrgCardModal";
import { toast } from "react-toastify";
import Filter from "./Components/Filter";
import OrgProductTable from "./Components/OrgCardTable";
import { addOrgCardMapping, listOrgCardRequest, markCardProductDefaultRequest } from "../../../utils/api";

const OrgCardManangement = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [cardProducts, setCardProducts] = useState([]);
  const [pagination, setPagination] = useState({
    pageNo: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
  });
  const fetchCardProducts = useCallback(
    async (
      payload = {
        productId: null,
        orgCode: null,
        status: null, // default = show all
        pageNo: 0,
        pageSize: 10,
      },
    ) => {
      setTableLoading(true);

      try {
        const apiPayload = {
          productId: payload.productId || null,
          orgCode: payload.orgCode || null,
          status: payload.status || null,
          pageNo: Number(payload.pageNo) || 0,
          pageSize: payload.pageSize || 10,
        };

        const res = await listOrgCardRequest(apiPayload); // ← your new API
        const responsePayload = res?.data ?? res;

        if (responsePayload?.code === "0000" && responsePayload?.data) {
          const content = responsePayload.data.content || [];

          setCardProducts(content);

          setPagination({
            pageNo: responsePayload.data.number,
            pageSize: apiPayload.pageSize,
            totalElements: responsePayload.data.totalElements,
            totalPages: responsePayload.data.totalPages,
          });
        } else {
          console.error("Failed to fetch org card list:", responsePayload);
          toast.error("Failed to load card products.", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error("Fetch org card products error:", error);
        toast.error("Network error while fetching products.", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setTableLoading(false);
      }
    },
    [],
  );
  useEffect(() => {
    fetchCardProducts(); // loads all data initially
  }, []);
  const handleModalClose = () => {
    setModalOpen(false);
  };
  const handleAddCardProduct = () => {
    setModalOpen(true);
  };
  const handleModalSubmit = useCallback(
    async (formData) => {
      setModalSubmitting(true);

      const payload = {
        orgCode: formData.orgCode,
        productCodeUuid: formData.productCodeUuid,
        virtualCardAssignmentEnabled: true,
        physicalCardAssignmentEnabled: true,
      };

      try {
        const res = await addOrgCardMapping(payload);
        const data = res?.data ?? res;

        if (data?.code === "0000") {
          toast.success("Mapping created successfully!", {
            position: "top-right",
            autoClose: 3000,
          });

          setModalOpen(false);

          // Reload list
          fetchCardProducts();
        } else {
          toast.error(data?.msg || "Failed to create mapping", {
            position: "top-right",
          });
        }
      } catch (error) {
        console.error("Org card mapping failed:", error);

        toast.error(error?.response?.data?.msg || "Network error creating mapping", { position: "top-right" });
      } finally {
        setModalSubmitting(false);
      }
    },
    [fetchCardProducts],
  );

  const handlePageChange = useCallback(
    (newPageNo) => {
      fetchCardProducts({
        productId: null,
        orgCode: null,
        status: null,
        pageNo: Number(newPageNo),
        pageSize: pagination.pageSize,
      });
    },
    [fetchCardProducts, pagination.pageSize],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      fetchCardProducts({
        productId: null,
        orgCode: null,
        status: null,
        pageNo: 0,
        pageSize: newPageSize,
      });
    },
    [fetchCardProducts],
  );

  const handleFilter = useCallback(
    (filterPayload) => {
      console.log("filter called with:", filterPayload);
      fetchCardProducts(filterPayload);
    },
    [fetchCardProducts],
  );
  const handleToggleDefault = useCallback(
    async (item) => {
      if (!item?.id) return;
      setTableLoading(true);
      try {
        const res = await markCardProductDefaultRequest({ id: item.id });
        const payload = res?.data ?? res;
        if (payload?.code === "0000") {
          toast.success(payload?.message || "Marked as default", {
            position: "top-right",
            autoClose: 3000,
          });
          // refresh current page
          fetchCardProducts({
            productId: null,
            orgCode: null,
            status: null,
            pageNo: pagination.pageNo,
            pageSize: pagination.pageSize || 10,
          });
        } else {
          const msg = payload?.msg || payload?.message || "Failed to mark default";
          toast.error(msg, { position: "top-right", autoClose: 3000 });
        }
      } catch (err) {
        console.error("Toggle default failed:", err);
        toast.error(err?.message || "Network error", { position: "top-right", autoClose: 3000 });
      } finally {
        setTableLoading(false);
      }
    },
    [fetchCardProducts, pagination.pageNo, pagination.pageSize],
  );
  return (
    <>
      <div className="flex flex-center space-between mb10">
        <Heading size="xxl">Org Card Product Management</Heading>

        <ButtonOutline rg className="flex flex-center" primary onClick={handleAddCardProduct}>
          Add New Card Product Mapping
        </ButtonOutline>
      </div>
      <Box
        sx={{
          p: 3,
          maxWidth: 1200,
          margin: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Filter onFilter={handleFilter} />
        {tableLoading ? (
          <CircularProgress />
        ) : (
          <OrgProductTable
            data={cardProducts}
            pagination={{
              // GenericMUITable + MUI Pagination use 1-based page; API stores 0-based pageNo
              currentPage: pagination.pageNo + 1,
              totalPages: pagination.totalPages,
              totalRecords: pagination.totalElements,
              itemsPerPage: pagination.pageSize,
              onPageChange: (page1Based) => handlePageChange(page1Based - 1),
              onItemsPerPageChange: (e) => {
                const newSize = parseInt(e.target.value, 10);
                handlePageSizeChange(newSize);
              },
            }}
            onToggleDefault={handleToggleDefault}
            loading={tableLoading}
          />
        )}{" "}
        <AddOrgCardModal
          open={modalOpen}
          handleClose={handleModalClose}
          onSubmit={handleModalSubmit}
          isSubmitting={modalSubmitting}
        />
      </Box>
    </>
  );
};

export default OrgCardManangement;
